'use client';

import Link from 'next/link';
import { useEffect, useMemo, useRef } from 'react';
import useSWRInfinite from 'swr/infinite';
import Photo from '@components/elements/Photo';
import Ring from '@components/elements/Ring';
import { formatPrice } from '@utils/price';
import { formatDuration } from '@lib/duration';
import { todayInArgentina, useNextAvailableDates } from '@contexts/next-available';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { highlightStore } from './highlight-store';

const API_URL = process.env.NEXT_PUBLIC_CYCLONE_API_URL;
const PAGE_SIZE = 24;

const fetcher = (url) =>
  fetch(url).then((res) => {
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  });

// Alturas que se alternan para que la grilla respire como un muro de fotos (las variantes son
// cuadradas; el recorte es central).
const HEIGHTS = [236, 176, 190, 236, 180, 190];

const NO_WORKS = [];

/**
 * Vista «Trabajos»: fotos de trabajos dentro de la zona del mapa, cada una con su servicio y precio.
 * Cyclone las intercala de a una por negocio y rota el orden por día: nadie queda primero fijo.
 * Un rubro filtra por `category_id`; el texto que escribió la persona va como `search`.
 */
export default function WorksGrid({ boundsParams, query, categoryId, todayOnly, selectedId, scrollRoot }) {
  const getKey = (pageIndex, previous) => {
    if (!boundsParams) return null;
    if (previous && previous.works.length < PAGE_SIZE) return null;
    let url = `${API_URL}/marketplace/works?${boundsParams}&page=${pageIndex + 1}&limit=${PAGE_SIZE}`;
    if (categoryId) url += `&category_id=${categoryId}`;
    if (query) url += `&search=${encodeURIComponent(query)}`;
    return url;
  };
  const { data, error, size, setSize, isValidating } = useSWRInfinite(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateFirstPage: false,
    keepPreviousData: true
  });

  const works = useMemo(() => (data ? data.flatMap((page) => page.works) : NO_WORKS), [data]);
  const total = data?.[0]?.total_count ?? 0;
  const reachedEnd = data && works.length >= total;

  // «Turnos hoy»: quedan los trabajos de negocios con turno hoy (los que todavía no se saben, también).
  const usernames = useMemo(
    () => (todayOnly ? [...new Set(works.map((w) => w.username))] : NO_WORKS),
    [todayOnly, works]
  );
  const dates = useNextAvailableDates(usernames);
  const today = todayInArgentina();
  const shown = todayOnly
    ? works.filter((w) => dates[w.username] === undefined || (dates[w.username] && dates[w.username] <= today))
    : works;

  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isValidating && !reachedEnd) setSize((s) => s + 1);
      },
      { root: scrollRoot?.current || null, rootMargin: '0px 0px 600px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isValidating, reachedEnd, setSize, scrollRoot]);

  if (error) {
    return <p className="works-empty">No pudimos cargar los trabajos. Probá de nuevo en un momento.</p>;
  }
  if (!data) {
    return (
      <div className="works-grid" aria-busy="true">
        {HEIGHTS.map((h, i) => (
          <div key={i} className="work work--loading" style={{ height: h }} />
        ))}
      </div>
    );
  }
  if (shown.length === 0) {
    return (
      <p className="works-empty">
        {todayOnly
          ? 'En esta zona no hay trabajos de negocios con turnos hoy. Sacá «Turnos hoy» o mové el mapa.'
          : 'Todavía no hay fotos de trabajos en esta zona. Mové el mapa o mirá la pestaña Negocios.'}
      </p>
    );
  }

  return (
    <>
      <div className={`works-grid ${isValidating && size === 1 ? 'is-refreshing' : ''}`}>
        {shown.map((work, i) => {
          const id = `agora-${work.vendor_id}`;
          const title = work.caption || work.service_name;
          const detail = [
            work.price ? formatPrice(work.price) : null,
            work.duration ? formatDuration(work.duration) : null
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Link
              key={`${work.vendor_id}-${work.url}`}
              href={`/negocios/${work.username}`}
              className={`work ${selectedId === id ? 'is-selected' : ''}`}
              style={{ height: HEIGHTS[i % HEIGHTS.length] }}
              onMouseEnter={() => highlightStore.set(id)}
              onMouseLeave={() => highlightStore.set(null)}
              onClick={() =>
                trackEvent(EVENTS.DISCOVERY_VENDOR_CARD_CLICKED, {
                  vendor_username: work.username,
                  source: 'works',
                  card_type: 'work'
                })
              }
            >
              <Photo src={work.url} size={500} alt={title ? `${title}, de ${work.name}` : `Trabajo de ${work.name}`} />
              <Ring src={work.avatar} name={work.name} size={24} className="work__who" />
              {(title || detail) && (
                <span className="work-tag">
                  <b>{title || work.name}</b>
                  {detail && <span>{detail}</span>}
                </span>
              )}
            </Link>
          );
        })}
      </div>
      <div ref={sentinel} className="h-5" />
      {reachedEnd && shown.length > 8 && <p className="works-end">No hay más trabajos en esta zona</p>}
    </>
  );
}
